/**
 * ShareDialog — create & manage dashboard share links.
 * Shell matches AddPageDialog (zoned header, hairline borders).
 */

import { useEffect, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { usePropertySafe } from "@/lib/contexts/PropertyContext"
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext"
import { useAuth } from "@/lib/contexts/AuthContext"
import { useSubscription } from "@/lib/contexts/SubscriptionContext"
import { CreateOrUpgradeButton } from "@/components/shared/plan-usage/CreateOrUpgradeButton"
import {
  createMagicLink,
  getMagicLinks,
  revokeMagicLink,
} from "@/lib/api/share"
import { ShareLinksList } from "./ShareLinksList"
import { EmptyState } from "@/components/shared/layout"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { copyTextToClipboard } from "@/lib/utils/clipboard"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Link01Icon,
  Loading01Icon,
  PlusSignIcon,
} from "@hugeicons/core-free-icons"

type ExpiryOption = "24-hours" | "2-weeks" | "forever"

const EXPIRY_OPTIONS: {
  value: ExpiryOption
  label: string
  hint: string
}[] = [
  { value: "24-hours", label: "24 hours", hint: "Quick review" },
  { value: "2-weeks", label: "2 weeks", hint: "Default" },
  { value: "forever", label: "Never", hint: "Until revoked" },
]

interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ShareDialog({ open, onOpenChange }: ShareDialogProps) {
  const propertyContext = usePropertySafe()
  const workspaceContext = useWorkspaceSafe()
  const { token } = useAuth()
  // View-only accounts (canceled or lapsed trial) keep and revoke their links
  // but can't make new ones: a link shares data the account no longer pays for.
  const { isReadOnly } = useSubscription()
  const queryClient = useQueryClient()
  const [expiry, setExpiry] = useState<ExpiryOption>("2-weeks")
  const [isCreating, setIsCreating] = useState(false)

  const property = propertyContext?.property || null
  const workspacePropertyId = workspaceContext?.propertyId
  const propertyId = property?.propertyId || workspacePropertyId
  const domain =
    property?.domain ||
    (property as { name?: string } | null)?.name ||
    undefined

  useEffect(() => {
    if (!open) {
      setIsCreating(false)
      setExpiry("2-weeks")
    }
  }, [open])

  const { data: magicLinks, isLoading } = useQuery({
    queryKey: ["magicLinks"],
    queryFn: () => getMagicLinks(token!),
    enabled: open && !!token,
  })

  const propertyMagicLinks =
    magicLinks?.filter((link) => link.propertyId === propertyId) || []

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!propertyId || !token) throw new Error("Missing data")
      return createMagicLink(propertyId, expiry, token)
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["magicLinks"] })
      const copied = await copyTextToClipboard(data.link)
      toast.success(
        copied
          ? "Share link created and copied to clipboard"
          : "Share link created — copy it from the list below"
      )
      setIsCreating(false)
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create share link")
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (magicLinkId: string) =>
      revokeMagicLink(propertyId!, magicLinkId, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["magicLinks"] })
      toast.success("Share link revoked")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to revoke share link")
    },
  })

  const handleCopy = async (link: string) => {
    const copied = await copyTextToClipboard(link)
    if (copied) {
      toast.success("Share link copied to clipboard")
    } else {
      toast.error("Couldn't copy share link")
    }
    return copied
  }

  const activeCount = propertyMagicLinks.filter(
    (l) => !l.expired && (l.forever || new Date(l.expiryDate) >= new Date())
  ).length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-xl max-h-[85vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-5 pt-5 pb-4 border-b border-border/40 space-y-0 text-left">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex size-9 items-center justify-center rounded-lg bg-muted/40 border border-border/40 shrink-0">
                <HugeiconsIcon
                  icon={Link01Icon}
                  size={16}
                  className="text-muted-foreground"
                />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-base font-medium text-foreground">
                  Share dashboard
                </DialogTitle>
                <DialogDescription className="text-[12.5px] text-muted-foreground mt-1 leading-relaxed">
                  Anyone with a link can view this dashboard. No sign-in
                  required.
                </DialogDescription>
                {domain && (
                  <p className="mt-1.5 text-[11px] text-muted-foreground truncate">
                    <span className="text-foreground/80 font-medium">{domain}</span>
                    {activeCount > 0 && (
                      <>
                        <span className="mx-1.5 text-border">·</span>
                        {activeCount} active
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>
            {!isCreating && (
              <CreateOrUpgradeButton
                atLimit={isReadOnly}
                noun="share links"
                size="sm"
                className="h-8 gap-1.5 shrink-0"
                onClick={() => setIsCreating(true)}
                disabled={!propertyId || createMutation.isPending}
              >
                <HugeiconsIcon icon={PlusSignIcon} size={14} />
                Create link
              </CreateOrUpgradeButton>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 px-5 py-4 space-y-4">
          {isCreating && (
            <div className="rounded-xl border border-border/30 bg-muted/10 p-4 space-y-4">
              <div>
                <p className="text-xs font-semibold text-muted-foreground">
                  Expiration
                </p>
                <p className="text-[12px] text-muted-foreground mt-1">
                  Choose how long this link stays valid
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {EXPIRY_OPTIONS.map((opt) => {
                  const selected = expiry === opt.value
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setExpiry(opt.value)}
                      className={cn(
                        "rounded-lg border px-2.5 py-2.5 text-left transition-colors",
                        selected
                          ? "border-foreground/80 bg-muted/40"
                          : "border-border/30 bg-card/60 hover:bg-muted/40"
                      )}
                    >
                      <span
                        className={cn(
                          "block text-[12.5px] font-medium leading-none",
                          selected ? "text-foreground" : "text-foreground/90"
                        )}
                      >
                        {opt.label}
                      </span>
                      <span className="mt-1 block text-[11px] text-muted-foreground leading-none">
                        {opt.hint}
                      </span>
                    </button>
                  )
                })}
              </div>

              <div className="flex items-center justify-end gap-2 pt-0.5">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setIsCreating(false)}
                  disabled={createMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="h-8 gap-1.5"
                  onClick={() => createMutation.mutate()}
                  disabled={createMutation.isPending || !propertyId}
                >
                  {createMutation.isPending ? (
                    <>
                      <HugeiconsIcon
                        icon={Loading01Icon}
                        size={14}
                        className="animate-spin"
                      />
                      Creating…
                    </>
                  ) : (
                    <>
                      <HugeiconsIcon icon={Link01Icon} size={14} />
                      Create & copy
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <HugeiconsIcon
                icon={Loading01Icon}
                size={18}
                className="animate-spin text-muted-foreground"
              />
            </div>
          ) : propertyMagicLinks.length > 0 ? (
            <div className="space-y-2.5">
              <div className="flex items-center gap-2">
                <span className="size-1.5 rounded-full bg-live shrink-0" />
                <span className="text-xs font-semibold text-muted-foreground">
                  Active links
                </span>
                <span className="h-3 w-px bg-border/60" />
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  {propertyMagicLinks.length}
                </span>
              </div>
              <ShareLinksList
                magicLinks={propertyMagicLinks}
                onCopy={handleCopy}
                onDelete={(id) => deleteMutation.mutate(id)}
                isDeleting={deleteMutation.isPending}
              />
            </div>
          ) : (
            !isCreating && (
              <EmptyState
                icon={<HugeiconsIcon icon={Link01Icon} size={28} />}
                title="No share links yet"
                description="Create a link to share analytics with clients or teammates. View-only, no account needed."
                className="py-10 border border-dashed border-border/40 rounded-xl bg-muted/10"
                action={
                  <Button
                    size="sm"
                    className="h-8 gap-1.5"
                    onClick={() => setIsCreating(true)}
                    disabled={!propertyId}
                  >
                    <HugeiconsIcon icon={PlusSignIcon} size={14} />
                    Create first link
                  </Button>
                }
              />
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
